import { Head, Link, router, usePage } from "@inertiajs/react"
import { useState } from "react"

import { WorkflowStatusBadge, deriveWorkflowStatus } from "@/components/hpi"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Customer {
  id: number
  display_name: string
}

interface TimeEntry {
  id: number
  date: string | null
  service_type: string | null
  user_name: string | null
  contact_name: string | null
  notes: string | null
  started_at: string | null
  ended_at: string | null
  otj_minutes: number
  admin_minutes: number
  travel_minutes: number
  total_minutes: number
  mileage: number
  marketing_log_id: number | null
  submitted: boolean
  approved: boolean
  paid: boolean
  created_at: string | null
  submitted_at: string | null
  approved_at: string | null
  can_delete: boolean
}

interface Summary {
  total_minutes: number
  total_mileage: number
  entry_count: number
}

interface Filters {
  start_date: string | null
  end_date: string | null
  user_id: number | null
  service_type_id: number | null
}

interface FilterOptions {
  users: { id: number; name: string }[]
  service_types: { id: number; name: string }[]
}

interface Props {
  customer: Customer
  time_entries: TimeEntry[]
  summary: Summary
  filters: Filters
  filter_options: FilterOptions
}

function formatTimeHM(minutes: number): string {
  if (minutes <= 0) return ""
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m}m`
  return `${h}h ${m.toString().padStart(2, "0")}m`
}

function statusBadge(entry: TimeEntry) {
  return <WorkflowStatusBadge status={deriveWorkflowStatus(entry)} />
}

export default function Index({
  customer,
  time_entries,
  summary,
  filters,
  filter_options,
}: Props) {
  const { auth } = usePage().props
  const isAdmin = auth.user.is_admin!

  const [startDate, setStartDate] = useState(filters.start_date ?? "")
  const [endDate, setEndDate] = useState(filters.end_date ?? "")
  const [userId, setUserId] = useState(filters.user_id?.toString() ?? "")
  const [serviceTypeId, setServiceTypeId] = useState(
    filters.service_type_id?.toString() ?? "",
  )
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteEntryId, setDeleteEntryId] = useState<number | null>(null)
  const [deleteReason, setDeleteReason] = useState("")
  const [deleting, setDeleting] = useState(false)

  const handleDelete = () => {
    if (!deleteReason.trim() || !deleteEntryId) return
    setDeleting(true)
    router.delete(`/customers/${customer.id}/time_entries/${deleteEntryId}`, {
      data: { reason: deleteReason.trim() },
      onFinish: () => {
        setDeleting(false)
        setDeleteOpen(false)
        setDeleteReason("")
        setDeleteEntryId(null)
      },
    })
  }

  const openDeleteModal = (entryId: number) => {
    setDeleteEntryId(entryId)
    setDeleteReason("")
    setDeleteOpen(true)
  }

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Customers", href: "/customers" },
    { title: customer.display_name, href: `/customers/${customer.id}` },
    { title: "Time Entries", href: `/customers/${customer.id}/time_entries` },
  ]

  const hasMileage = time_entries.some((e) => e.mileage > 0)
  const hasFilters =
    filters.start_date ??
    filters.end_date ??
    filters.user_id ??
    filters.service_type_id
  const showFilterBar =
    time_entries.length > 10 ||
    !!hasFilters ||
    filter_options.users.length > 1 ||
    filter_options.service_types.length > 1

  const handleFilter = () => {
    router.get(
      `/customers/${customer.id}/time_entries`,
      {
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        user_id: userId || undefined,
        service_type_id: serviceTypeId || undefined,
      },
      { preserveState: true },
    )
  }

  const handleClearFilters = () => {
    setStartDate("")
    setEndDate("")
    setUserId("")
    setServiceTypeId("")
    router.get(
      `/customers/${customer.id}/time_entries`,
      {},
      { preserveState: true },
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Time Entries - ${customer.display_name}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            Time Entries for {customer.display_name}
          </h1>
          <Link
            href={`/customers/${customer.id}/time_entries/new`}
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded-md px-4 py-2 text-sm font-medium text-white"
          >
            Add Time
          </Link>
        </div>

        {showFilterBar && (
          <div className="border-hpi-light-gray mb-4 flex flex-wrap items-end gap-3 rounded border bg-white p-3">
            <div>
              <label className="text-hpi-med-gray mb-1 block text-xs">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border-hpi-light-gray rounded border px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label className="text-hpi-med-gray mb-1 block text-xs">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border-hpi-light-gray rounded border px-2 py-1 text-sm"
              />
            </div>
            {filter_options.users.length > 1 && (
              <div>
                <label className="text-hpi-med-gray mb-1 block text-xs">
                  Employee
                </label>
                <select
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  className="border-hpi-light-gray rounded border px-2 py-1 text-sm"
                >
                  <option value="">All</option>
                  {filter_options.users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {filter_options.service_types.length > 1 && (
              <div>
                <label className="text-hpi-med-gray mb-1 block text-xs">
                  Type
                </label>
                <select
                  value={serviceTypeId}
                  onChange={(e) => setServiceTypeId(e.target.value)}
                  className="border-hpi-light-gray rounded border px-2 py-1 text-sm"
                >
                  <option value="">All</option>
                  {filter_options.service_types.map((st) => (
                    <option key={st.id} value={st.id}>
                      {st.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <button
              onClick={handleFilter}
              className="bg-hpi-med-blue hover:bg-hpi-active-blue rounded px-3 py-1 text-sm text-white"
            >
              Search
            </button>
            {hasFilters && (
              <button
                onClick={handleClearFilters}
                className="text-hpi-med-gray hover:text-hpi-dark-gray text-sm underline"
              >
                Clear
              </button>
            )}
          </div>
        )}

        {time_entries.length > 0 && (
          <div className="mb-4 rounded-lg bg-white p-4 shadow">
            <span className="text-hpi-med-gray text-sm">Total: </span>
            <span className="text-lg font-bold">
              {formatTimeHM(summary.total_minutes)}
            </span>
            <span className="text-hpi-med-gray ml-4 text-sm">
              {summary.entry_count}{" "}
              {summary.entry_count === 1 ? "entry" : "entries"}
            </span>
            {summary.total_mileage > 0 && (
              <span className="text-hpi-med-gray ml-4 text-sm">
                {summary.total_mileage} miles
              </span>
            )}
          </div>
        )}

        {time_entries.length > 0 ? (
          <table className="border-hpi-light-gray w-full border-collapse border text-sm">
            <thead>
              <tr className="bg-hpi-pale-gray">
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Date
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Type
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Description
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-right">
                  Total
                </th>
                {hasMileage && (
                  <th className="border-hpi-light-gray border px-4 py-2 text-right">
                    Miles
                  </th>
                )}
                <th className="border-hpi-light-gray border px-4 py-2 text-center">
                  Status
                </th>
                {isAdmin && (
                  <>
                    <th className="border-hpi-light-gray border px-4 py-2 text-left">
                      Added
                    </th>
                    <th className="border-hpi-light-gray border px-4 py-2 text-left">
                      Submitted
                    </th>
                    <th className="border-hpi-light-gray border px-4 py-2 text-left">
                      Approved
                    </th>
                  </>
                )}
                <th className="border-hpi-light-gray border px-4 py-2 text-center" />
              </tr>
            </thead>
            <tbody>
              {time_entries.map((entry) => (
                <tr key={entry.id} className="hover:bg-hpi-pale-blue">
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {entry.marketing_log_id ? (
                      <Link
                        href={`/customers/${customer.id}/logs/${entry.marketing_log_id}`}
                        className="text-hpi-active-blue hover:underline"
                      >
                        {entry.date}
                      </Link>
                    ) : (
                      entry.date
                    )}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {entry.service_type}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <div className="font-medium">{entry.user_name}</div>
                    {entry.contact_name && (
                      <div className="text-hpi-med-gray text-xs">
                        {entry.contact_name}
                      </div>
                    )}
                    {entry.otj_minutes > 0 && (
                      <div className="text-hpi-med-gray text-xs">
                        {entry.started_at} to {entry.ended_at}
                        {entry.travel_minutes > 0 &&
                          ` | Travel: ${formatTimeHM(entry.travel_minutes)}`}
                      </div>
                    )}
                    {entry.otj_minutes === 0 &&
                      (entry.admin_minutes > 0 || entry.travel_minutes > 0) && (
                        <div className="text-hpi-med-gray text-xs">
                          {entry.admin_minutes > 0 &&
                            `Block: ${formatTimeHM(entry.admin_minutes)}`}
                          {entry.admin_minutes > 0 &&
                            entry.travel_minutes > 0 &&
                            " | "}
                          {entry.travel_minutes > 0 &&
                            `Travel: ${formatTimeHM(entry.travel_minutes)}`}
                        </div>
                      )}
                    {entry.notes && (
                      <div className="text-hpi-med-gray text-xs italic">
                        {entry.notes}
                      </div>
                    )}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2 text-right">
                    {formatTimeHM(entry.total_minutes)}
                  </td>
                  {hasMileage && (
                    <td className="border-hpi-light-gray border px-4 py-2 text-right">
                      {entry.mileage > 0 ? entry.mileage : ""}
                    </td>
                  )}
                  <td className="border-hpi-light-gray border px-4 py-2 text-center">
                    {statusBadge(entry)}
                  </td>
                  {isAdmin && (
                    <>
                      <td className="border-hpi-light-gray border px-4 py-2 text-sm">
                        {entry.created_at}
                      </td>
                      <td className="border-hpi-light-gray border px-4 py-2 text-sm">
                        {entry.submitted_at}
                      </td>
                      <td className="border-hpi-light-gray border px-4 py-2 text-sm">
                        {entry.approved_at}
                      </td>
                    </>
                  )}
                  <td className="border-hpi-light-gray border px-2 py-2 text-center">
                    {entry.can_delete && (
                      <button
                        onClick={() => openDeleteModal(entry.id)}
                        className="text-hpi-red hover:text-hpi-red/80 text-xs"
                        title="Delete time entry"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-hpi-med-gray">No time entries found.</p>
        )}

        <div className="mt-4">
          <Link
            href={`/customers/${customer.id}`}
            className="text-hpi-active-blue hover:underline"
          >
            Back to Customer
          </Link>
        </div>
      </div>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Time Entry</DialogTitle>
            <DialogDescription>
              Why are you deleting this time entry? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <textarea
            className="border-hpi-light-gray w-full rounded border p-2 text-sm"
            rows={4}
            placeholder="Enter reason for deletion..."
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            required
          />
          <DialogFooter>
            <button
              type="button"
              className="border-hpi-light-gray rounded border px-4 py-2 text-sm"
              onClick={() => setDeleteOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="bg-hpi-red hover:bg-hpi-red/80 rounded px-4 py-2 text-sm text-white disabled:opacity-50"
              onClick={handleDelete}
              disabled={!deleteReason.trim() || deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  )
}
